import { GridColDef } from '@mui/x-data-grid-pro';
import { DecomModel } from '../../../../types';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import AvatarChip from '../../../shared/avatar-chip';
import DecomPoliciesGridToolbar from './decom-policies-grid-toolbar';
import { useMemo, useState } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useDecomPolicyActions } from './hooks/use-decom-policy-actions';
import { Messages } from '../../../utils/message-constants';
import { getAllDecomModels, UserKeys } from '../../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import { useUserSettings } from '../../../utils/use-user-settings';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import DeleteDecomPoliciesDialog from './decom-policy-grid-actions/delete-decom-dialog';
import EnableDecomPoliciesDialog from './decom-policy-grid-actions/enable-decom-dialog';
import RunDecomPoliciesDialog from './decom-policy-grid-actions/run-decom-policies-dialog';
import DisableDecomPoliciesDialog from './decom-policy-grid-actions/disable-decom-dialog';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import StatusLabel from '../../../shared/status-label';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Edit from '@mui/icons-material/Edit';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import PolicyRounded from '@mui/icons-material/PolicyRounded';

export default function DecomPoliciesGrid() {
  const [openDeletePolicy, setOpenDeletePolicy] = useState(false);
  const [openRunDecomPolicies, setOpenRunDecomPolicies] = useState(false);
  const [openEnablePolicies, setOpenEnablePolicies] = useState(false);
  const [openDisablePolicies, setOpenDisablePolicies] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { openDrawer, handleEditPolicy, handleClonePolicy } =
    useDecomPolicyActions();

  const { toolsRoles, changeLogRoles, viewType } = useUserSettings();

  const {
    data: decomModels,
    isFetching: isFetchingDecomModels,
    isLoading: isLoadingDecomModels,
  } = useQuery({
    queryKey: [UserKeys.DecomModels],
    queryFn: () => getAllDecomModels(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('decom models'),
    },
  });

  const columns: GridColDef<DecomModel>[] = useMemo(
    () => [
      {
        field: 'createdTime',
        headerName: 'Created Date',
        display: 'flex',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          if (value === 0) {
            return null;
          } else {
            return date;
          }
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 110,
        display: 'flex',
        type: 'boolean',
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => {
          const status = params.value ? 'ACTIVE' : 'DISABLED';
          return <StatusLabel status={status} />;
        },
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        width: 250,
        display: 'flex',
        renderCell: (params) => {
          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        display: 'flex',
        flex: 1,
      },
      {
        field: 'org',
        headerName: 'Orgs',
        display: 'flex',
        flex: 1,
        renderCell: (params) => {
          return (
            <Stack flexDirection="row" gap={1} sx={{ overflow: 'auto' }}>
              {params.value.map((target: string) => (
                <AvatarChip
                  key={target}
                  email={target}
                  type={'OrgUnit'}
                  onClick={() => {
                    if (location.pathname.includes('/policies')) {
                      navigate({
                        pathname: '../suspended-users',
                        search: createSearchParams({
                          policy: params.row.id,
                        }).toString(),
                      });
                    } else {
                      navigate({
                        pathname: './suspended-users',
                        search: createSearchParams({
                          policy: params.row.id,
                        }).toString(),
                      });
                    }
                  }}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: 'lastUpdatedBy',
        headerName: 'Last Updated By',
        width: 185,
        display: 'flex',
        renderCell: (params) => {
          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'lastUpdatedTime',
        headerName: 'Last Updated',
        width: 185,
        display: 'flex',
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          if (value === 0) {
            return null;
          } else {
            return date;
          }
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'notificationUser',
        headerName: 'Notification User',
        width: 185,
        display: 'flex',
        renderCell: (params) => {
          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'keepUnsuspended',
        headerName: 'Keep Unsuspended',
        width: 145,
        display: 'flex',
        type: 'boolean',
      },
      {
        field: 'sendSummary',
        headerName: 'Send Summary',
        width: 120,
        display: 'flex',
        type: 'boolean',
      },
      {
        field: 'triggerType',
        headerName: 'Trigger Type',
        width: 170,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: [
          { label: 'User Suspension', value: 'suspension' },
          { label: 'Org Unit Change', value: 'move' },
        ],
      },
      {
        field: 'actions',
        headerName: 'Actions',
        display: 'flex',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const canEdit = isAbleToSeeUIElement(
            toolsRoles,
            ToolsRoles.EditDecommissioningProcesses.value
          );
          const canClone = isAbleToSeeUIElement(
            toolsRoles,
            ToolsRoles.CreateDecommissioningProcesses.value
          );
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip
                arrow
                title={
                  !canEdit
                    ? Messages.Errors.Permission.Acting(
                        ToolsRoles.EditDecommissioningProcesses.name
                      )
                    : 'Edit'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!canEdit}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPolicy(params.row);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                arrow
                title={
                  !canClone
                    ? Messages.Errors.Permission.Acting(
                        ToolsRoles.CreateDecommissioningProcesses.name
                      )
                    : 'Clone'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!canClone}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClonePolicy(params.row);
                    }}
                  >
                    <LibraryAdd fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [
      location.pathname,
      navigate,
      toolsRoles,
      handleEditPolicy,
      handleClonePolicy,
    ]
  );

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'createdTime',
          sort: 'desc',
        },
      ],
    },
    columns: {
      columnVisibilityModel: {
        keepUnsuspended: false,
        sendSummary: false,
        triggerType: false,
        notificationUser: false,
        lastUpdatedTime: false,
        lastUpdatedBy: false,
      },
    },
  };

  const toolbarProps = useMemo(
    () => ({
      toolsRoles,
      setOpenDeletePolicy,
      setOpenRunDecomPolicies,
      setOpenDisablePolicies,
      setOpenEnablePolicies,
      selectedRows,
      decomModels: decomModels || [],
    }),
    [toolsRoles, selectedRows, decomModels]
  );

  return (
    <>
      <SharedDataGridClient
        gridKey={viewType.decomPoliciesGridStates?.activeGridState}
        slots={{
          toolbar: useToolbar(DecomPoliciesGridToolbar, toolbarProps),
          ...createGridOverlays('Decom Policies', <PolicyRounded />),
        }}
        disableRowSelectionOnClick
        checkboxSelection
        rows={decomModels || []}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        loading={isLoadingDecomModels || isFetchingDecomModels}
        gridStateKey="decomPoliciesGridStates"
        defaultGridState={defaultGridState}
        onRowClick={(row) => openDrawer(row.row)}
      />
      <>
        {selectedRows.length > 0 && openDeletePolicy ? (
          <DeleteDecomPoliciesDialog
            open={openDeletePolicy}
            handleClose={() => {
              setOpenDeletePolicy(false);
            }}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            changeLogRoles={changeLogRoles}
            models={decomModels || []}
          />
        ) : null}
        {selectedRows.length > 0 && openEnablePolicies ? (
          <EnableDecomPoliciesDialog
            open={openEnablePolicies}
            handleClose={() => {
              setOpenEnablePolicies(false);
            }}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            changeLogRoles={changeLogRoles}
            models={decomModels || []}
          />
        ) : null}
        {selectedRows.length > 0 && openDisablePolicies ? (
          <DisableDecomPoliciesDialog
            open={openDisablePolicies}
            handleClose={() => {
              setOpenDisablePolicies(false);
            }}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            changeLogRoles={changeLogRoles}
            models={decomModels || []}
          />
        ) : null}
        {selectedRows.length > 0 && openRunDecomPolicies ? (
          <RunDecomPoliciesDialog
            open={openRunDecomPolicies}
            handleClose={() => {
              setOpenRunDecomPolicies(false);
            }}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            changeLogRoles={changeLogRoles}
            models={decomModels || []}
          />
        ) : null}
      </>
    </>
  );
}
